import { useState } from 'react';
import { useMyProducts } from '@/hooks/useProducts';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { Package, Plus, Barcode, ShieldCheck, ShieldWarning, ArrowSquareOut } from '@phosphor-icons/react';
import { formatDate } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { productsApi } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import useAppStore from '@/stores/appStore';

export const MyProducts = () => {
  const { data: products, isLoading } = useMyProducts();
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColorAccent = tenantConfig?.brand_colour || '#002FA7';
  const purchaseUrl = tenantConfig?.product_purchase_url;
  const queryClient = useQueryClient();

  // Modal State
  const [isOpen, setIsOpen] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleRegisterProduct = async (e) => {
    e.preventDefault();
    if (!serialNumber.trim()) {
      toast.error('Please enter a serial number');
      return;
    }

    setSubmitting(true);
    try {
      await productsApi.register({ serial_number: serialNumber.trim() });
      toast.success('Product registered successfully!');
      setSerialNumber('');
      setIsOpen(false);
      queryClient.invalidateQueries({ queryKey: ['myProducts'] });
    } catch (error) {
      console.error('Registration failed:', error);
      const msg = error.response?.data?.detail || 'Failed to register product';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DashboardLayout role="customer">
      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-slate-900">My Products</h1>
            <p className="text-sm text-slate-500 mt-0.5">Manage your registered products and warranty info</p>
          </div>
          <div className="flex gap-2">
            {purchaseUrl && (
              <a href={purchaseUrl} target="_blank" rel="noopener noreferrer">
                <Button
                  variant="outline"
                  className="font-bold text-xs flex items-center gap-1.5 h-10 border-slate-300"
                  data-testid="shop-products-btn"
                >
                  <ArrowSquareOut size={16} weight="bold" />
                  Shop Products
                </Button>
              </a>
            )}
            <Button 
              onClick={() => setIsOpen(true)}
              data-testid="register-product-btn"
              className="font-bold text-white shadow h-10"
              style={{ backgroundColor: brandColorAccent }}
            >
              <Plus size={18} className="mr-1.5" weight="bold" />
              Register Product
            </Button>
          </div>
        </div>

        {isLoading ? (
          <LoadingSpinner />
        ) : products?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => {
              const isWarrantyActive = product.warranty_status === 'Active';
              return (
                <Card key={product.id} className="overflow-hidden border hover:shadow-md transition-all group" data-testid={`product-card-${product.serial_number}`}>
                  {/* Product Image or Placeholder */}
                  <div className="h-36 bg-gradient-to-br from-slate-100 to-slate-50 flex items-center justify-center relative overflow-hidden">
                    {product.image_url ? (
                      <img 
                        src={product.image_url} 
                        alt={product.product_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Package size={48} className="text-slate-300" />
                    )}
                    {/* Warranty Badge */}
                    <div className={`absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${
                      isWarrantyActive 
                        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                        : 'bg-red-100 text-red-800 border border-red-200'
                    }`}>
                      {isWarrantyActive ? <ShieldCheck size={12} weight="fill" /> : <ShieldWarning size={12} weight="fill" />}
                      {product.warranty_status}
                    </div>
                  </div>

                  {/* Product Info */}
                  <div className="p-4 space-y-3">
                    <div>
                      <h3 className="font-bold text-slate-900 leading-tight">{product.product_name}</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{product.category_name}</p>
                    </div>

                    <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                      <Barcode size={14} className="text-slate-400" />
                      <span className="text-xs font-mono font-bold text-slate-700">{product.serial_number}</span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {product.warranty_expiry && (
                        <div>
                          <p className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">Warranty Until</p>
                          <p className="font-bold text-slate-700">{formatDate(product.warranty_expiry)}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-slate-400 font-semibold uppercase tracking-wider text-[10px]">Service History</p>
                        <p className="font-bold text-slate-700">{product.service_count || 0} service(s)</p>
                      </div>
                    </div>

                    {(product.purchase_url || purchaseUrl) && (
                      <a 
                        href={product.purchase_url || purchaseUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg text-xs font-bold transition-colors border border-slate-200 hover:bg-slate-50 text-slate-600"
                      >
                        <ArrowSquareOut size={14} />
                        View on Website
                      </a>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="p-12 sm:p-16 text-center border-2 border-dashed border-slate-200 rounded-2xl bg-slate-50/50">
            <div className="p-4 bg-slate-100 rounded-2xl inline-flex mb-4">
              <Package size={40} className="text-slate-400" />
            </div>
            <h3 className="font-bold text-lg text-slate-700 mb-1">No products registered yet</h3>
            <p className="text-sm text-slate-500 mb-6 max-w-sm mx-auto">
              Register your products to track warranty status and service history.
            </p>
            <Button 
              onClick={() => setIsOpen(true)}
              data-testid="register-first-product-btn"
              className="font-bold text-white shadow px-6 h-11"
              style={{ backgroundColor: brandColorAccent }}
            >
              <Plus size={18} className="mr-2" />
              Register Your First Product
            </Button>
          </Card>
        )}
      </div>

      {/* Register Product Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">Register Product</DialogTitle>
            <DialogDescription className="text-xs">
              Enter your product's valid physical serial number to link it to your profile.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleRegisterProduct} className="space-y-4 mt-4">
            <div className="space-y-1.5">
              <Label htmlFor="cust-serial" className="text-xs font-bold uppercase tracking-wider text-slate-700">
                Product Serial Number *
              </Label>
              <div className="relative">
                <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="cust-serial"
                  placeholder="e.g. CL-2001 or AC-1001"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  className="pl-10 h-11 border-slate-300 font-mono text-slate-900"
                  required
                />
              </div>
              <p className="text-[10px] text-slate-400 font-semibold mt-1">
                Note: The serial number must be listed in our product catalog. Check your warranty card or product packaging.
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-slate-100 mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsOpen(false)}
                className="font-bold text-xs uppercase tracking-wider h-11 px-6 border-slate-300"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="font-bold text-xs uppercase tracking-wider h-11 px-6 text-white"
                style={{ backgroundColor: brandColorAccent }}
              >
                {submitting ? 'Registering...' : 'Register Product'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};